/**
 * Client helper to obtain a CID token from the BFF (`POST /api/cid-token`). The SPA
 * sends its Cognito tokens and the request payload; the BFF signs a JWS whose
 * payload_hash matches the backend request body. See md-files/eB2B-security-pattern-CID-token.md.
 */

import { getStoredAccessToken, getStoredIdToken, getUserEmail, getUserCuid } from './session';
import { refreshCognitoSession } from './refresh';

/** Resolve the CID-token endpoint, preserving any leading context path. */
const resolveCidTokenEndpoint = () => {
  if (typeof window === 'undefined' || !window.location) {
    return '/api/cid-token';
  }

  const { hostname, pathname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/api/cid-token';
  }

  const firstPathSegment = (pathname || '').split('/').filter(Boolean)[0];
  if (firstPathSegment && firstPathSegment !== 'api') {
    return `/${firstPathSegment}/api/cid-token`;
  }

  return '/api/cid-token';
};

export const CID_TOKEN_ENDPOINT = resolveCidTokenEndpoint();

// The BFF verifies the id_token, binds it to the access_token, and cross-checks the
// forwarded email/cuid against the verified id_token claims before minting the CID token.
const postCidTokenRequest = (accessToken, idToken, payload, email, cuid) =>
  fetch(CID_TOKEN_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(email ? { 'X-HSBC-User-Email': email } : {}),
      ...(cuid ? { 'X-HSBC-User-Cuid': cuid } : {}),
    },
    body: JSON.stringify({ accessToken, idToken, payload }),
  });

const parseCidTokenResponse = async (response) => {
  let responseText = '';
  try {
    responseText = await response.text();
  } catch {
    return {};
  }

  if (!responseText) {
    return {};
  }

  let parsed;
  try {
    parsed = JSON.parse(responseText);
  } catch {
    return {};
  }

  if (!parsed || typeof parsed !== 'object') {
    return {};
  }

  return {
    token: typeof parsed.token === 'string' ? parsed.token : undefined,
    error: typeof parsed.error === 'string' ? parsed.error : undefined,
  };
};

/** Request a CID token for the given payload; refreshes Cognito once on 401 and retries. */
export const requestCidToken = async (payload = {}) => {
  let accessToken = getStoredAccessToken();
  let idToken = getStoredIdToken();
  if (!accessToken) {
    throw new Error('No Cognito access token available; cannot request CID token.');
  }
  if (!idToken) {
    throw new Error('No Cognito id token available; cannot request CID token.');
  }

  let response = await postCidTokenRequest(accessToken, idToken, payload, getUserEmail(), getUserCuid());

  // Refresh once on 401 and retry.
  if (response.status === 401) {
    await refreshCognitoSession();
    accessToken = getStoredAccessToken();
    idToken = getStoredIdToken();
    if (accessToken && idToken) {
      response = await postCidTokenRequest(accessToken, idToken, payload, getUserEmail(), getUserCuid());
    }
  }

  const data = await parseCidTokenResponse(response);

  if (!response.ok || !data.token) {
    throw new Error((data && data.error) || `CID token request failed (${response.status}).`);
  }

  return data.token;
};

export default requestCidToken;
